import { isValidYear } from '../date';
import { PRODUCT_TYPES, SPECIAL_CASES, HANDLE_VARIATIONS } from './constants';

export function parseHandle(
  handle: string,
  providedMake: string,
  providedModel: string,
  typeString: string
): {
  make: string;
  model: string;
  model_detail: string | null;
  cab_size: string | null;
  type: string | null;
  seat_type: string | null; // NEW: Specific seat type (front/rear/third)
  display_color: string | null;
  year_generation: string | null;
  has_seat_type_variants: boolean; // NEW: Flag to indicate seat type variants
} {
  const isCarCover = typeString === 'Car Covers';
  const isSeatCover = typeString === 'Seat Covers';
  const isTruckCover = typeString === 'Truck Covers';

  // Normalize inputs
  const normalizedMake = providedMake.toLowerCase();
  const normalizedModel = providedModel.toLowerCase();
  const parts = handle.toLowerCase().split('-');
  const makeModelKey = `${normalizedMake}/${normalizedModel}`;
  const isTesla = handle.toLowerCase().startsWith('tesla');
  const isPolestar = handle.toLowerCase().startsWith('polestar');

  let expectedHandlePrefix: string;
  let validatedSuccessfully = false;

  // Skip validation for Tesla and Polestar
  if (isTesla || isPolestar) {
    expectedHandlePrefix = `${normalizedMake}-${normalizedModel}`;
    validatedSuccessfully = true;
  } else {
    // Try expected format first, then variations
    const standardFormat = `${normalizedMake}-${normalizedModel}`;
    const variations = HANDLE_VARIATIONS[makeModelKey] || [standardFormat];
    
    // Find the first variation that works
    for (const variation of variations) {
      if (handle.toLowerCase().startsWith(variation)) {
        expectedHandlePrefix = variation;
        validatedSuccessfully = true;
        break;
      }
    }

    if (!validatedSuccessfully) {
      const triedFormats = variations.join(', ');
      throw new Error(`Handle does not match provided make and model. Tried formats: ${triedFormats} but got '${handle.toLowerCase()}'`);
    }
  }

  const specialCase = SPECIAL_CASES.find(
    (caseItem) => caseItem.make === normalizedMake && caseItem.model === normalizedModel
  );

  // Extract the remaining parts of the handle after the make-model prefix
  const baseParts = expectedHandlePrefix.split('-').length;
  let remainingParts = parts.slice(baseParts);

  let modelDetail: string | null = null;
  const cabSize: string | null = null;
  let type: string | null = null;
  let seatType: string | null = null; // NEW
  let displayColor: string | null = null;
  let yearGeneration: string | null = null;
  let hasSeatTypeVariants = false; // NEW

  // Handle model details for special cases
  if (specialCase) {
    // First, try to identify if this is a model detail part
    const modelDetailParts: string[] = [];
    let currentIndex = 0;

    // Check if the first part is a basic model detail (1500, 2500, 3500)
    if (/^[123]500(hd)?$/.test(remainingParts[0])) {
      modelDetailParts.push(remainingParts[0]);
      currentIndex++;

      // Check for 'classic' or 'trx' suffix
      if (remainingParts[currentIndex] === 'classic' || remainingParts[currentIndex] === 'trx') {
        modelDetailParts.push(remainingParts[currentIndex]);
        currentIndex++;
      }
    }

    if (modelDetailParts.length > 0) {
      modelDetail = modelDetailParts.join(' ');
      remainingParts = remainingParts.slice(currentIndex);
    } else {
      modelDetail = 'Standard';
    }
  }

  // Handle Ford F-250 Super Duty -- Not exact match the second word in the Product Title is the Model Name.
  if (isSeatCover) {
    const patternForChecking = ['custom', 'seat', 'covers'];

    const indexForChecking = patternForChecking.findIndex(
      (_, i) =>
        remainingParts.slice(i, i + patternForChecking.length).join(',') ===
        patternForChecking.join(',')
    );

    if (indexForChecking !== -1) {
      remainingParts = remainingParts.slice(indexForChecking); // Keep from 'custom' onward
    } else {
      remainingParts = remainingParts; // No change if pattern not found
    }
  }

  // Handle different product types and their specific patterns
  if (isSeatCover && remainingParts[0] === 'custom') {
    // NEW: Detect seat type patterns
    const seatTypePatterns = ['front', 'rear', 'third'];
    const foundSeatType = seatTypePatterns.find((pattern) => remainingParts.includes(pattern));

    if (foundSeatType) {
      seatType = foundSeatType;
      hasSeatTypeVariants = true;

      // Extract type without the seat type specifier
      // e.g., "custom-front-seat-covers" -> "custom seat covers"
      const typeIndex = remainingParts.findIndex((part) => part === foundSeatType);
      const typeParts = [
        ...remainingParts.slice(0, typeIndex),
        ...remainingParts.slice(typeIndex + 1, typeIndex + 3),
      ];
      type = typeParts.join(' ');
      remainingParts = remainingParts.slice(typeIndex + 3);
    } else {
      // Original logic for handles without seat type
      type = `${remainingParts[1]} ${remainingParts[2]}` || PRODUCT_TYPES.SEAT_COVERS || null;
      remainingParts = remainingParts.slice(3);
    }

    // Handle seat cover specific pattern
    if (remainingParts.length >= 2) {
      displayColor = remainingParts[0];
      // Collect all remaining years
      yearGeneration = extractYearGeneration(remainingParts);
    }
  } else if (isTruckCover || (!isSeatCover && remainingParts.includes('truck'))) {
    // Handle truck cover pattern
    type = PRODUCT_TYPES.TRUCK_COVER;
    remainingParts = remainingParts.filter((part) => part !== 'truck' && part !== 'cover');

    if (remainingParts.length >= 1) {
      displayColor = remainingParts[0];
      // Collect all remaining years
      yearGeneration = extractYearGeneration(remainingParts);
    }
  } else if ((isCarCover || isSeatCover) && (isTesla || isPolestar)) {
    type = typeString;
    displayColor = isCarCover
      ? remainingParts[1] || null
      : isSeatCover
        ? remainingParts[2] || null
        : null;
    yearGeneration = remainingParts.length > 1 ? extractYearGeneration(remainingParts) : null;
  } else {
    // Non-custom case (e.g., car covers)
    type = `${remainingParts[0]} ${remainingParts[1]}` || null;
    displayColor = remainingParts[2] || null;
    yearGeneration = remainingParts.length > 3 ? extractYearGeneration(remainingParts) : null;
  }

  return {
    make: normalizedMake,
    model: normalizedModel,
    model_detail: modelDetail,
    cab_size: cabSize,
    type,
    seat_type: seatType, // NEW
    display_color: displayColor,
    year_generation: yearGeneration,
    has_seat_type_variants: hasSeatTypeVariants, // NEW
  };
}

function extractYearGeneration(remainingParts: string[]): string | null {
  const last = remainingParts[remainingParts.length - 1];
  const secondLast = remainingParts[remainingParts.length - 2];

  // Case: last two parts are both years like ["2022", "2024"]
  if (isValidYear(secondLast) && isValidYear(last)) {
    return `${secondLast}-${last}`;
  }

  // Case: only the last part is a single year
  if (isValidYear(last)) {
    return last;
  }

  // No valid year found
  return '';
}
